// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useLayoutEffect, useState } from 'react';
import styles from './styles.css.js';
/**
 * Returns how many trailing tokens fit fully next to the input; the rest collapse into the overflow trigger.
 */
export function useTokenOverflow({ enabled, tokenCount, tokenContentKey, measureRef, measureTriggerRef, tokensRef, inputRef, fieldWidth, layoutKey, }) {
    const [visibleCount, setVisibleCount] = useState(tokenCount);
    useLayoutEffect(() => {
        var _a, _b;
        const tokens = tokensRef.current;
        const input = inputRef.current;
        const measure = measureRef.current;
        if (measure) {
            measure.inert = true;
        }
        if (!enabled || !tokens || !input || !measure || !fieldWidth) {
            setVisibleCount(tokenCount);
            return;
        }
        // The tokens and the input share the available space, whatever the tokens currently take.
        const availableWidth = tokens.offsetWidth + input.offsetWidth;
        const gap = parseFloat(getComputedStyle(tokens).columnGap) || 0;
        const inputMinWidth = resolveLength(getComputedStyle(input).minInlineSize, fieldWidth);
        const triggerWidth = (_b = (_a = measureTriggerRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0;
        const tokenWidths = Array.from(measure.querySelectorAll(`.${styles['measurement-token']}`)).map(el => el.offsetWidth);
        const budget = availableWidth - inputMinWidth;
        const totalWidth = tokenWidths.reduce((sum, width) => sum + width + gap, -gap);
        setVisibleCount(totalWidth <= budget ? tokenWidths.length : countFittingFromEnd(tokenWidths, budget - triggerWidth - gap, gap));
    }, [enabled, tokenCount, tokenContentKey, fieldWidth, layoutKey, measureRef, measureTriggerRef, tokensRef, inputRef]);
    return Math.min(visibleCount, tokenCount);
}
function countFittingFromEnd(widths, budget, gap) {
    let used = -gap;
    let count = 0;
    for (let i = widths.length - 1; i >= 0 && used + gap + widths[i] <= budget; i--) {
        used += gap + widths[i];
        count++;
    }
    return count;
}
function resolveLength(value, base) {
    const number = parseFloat(value);
    if (isNaN(number)) {
        return 0;
    }
    return value.trim().endsWith('%') ? (base * number) / 100 : number;
}
//# sourceMappingURL=use-token-overflow.js.map